import { PropsWithChildren } from 'react';

import type Big from 'big.js';

import {
  EllipsisWithTooltip,
  Skeleton,
  useTabsState,
  Box,
} from '@cast/design-system';
import { WorkloadTrafficDestination } from '@cast/types';
import { bytesToUnit, getCurrencyPrecision } from '@cast/utils';

import { MetricsBox } from 'components/boxes';
import { Money } from 'components/money';

import { NetworkResourceType } from '../../../_types';
import { TOP_DESTINATIONS_COLOR_MAP } from '../_constants';
import { sumResource } from '../_utils';

const Container = ({ children }: PropsWithChildren<any>) => (
  <Box data-testid="top-destinations-summary">{children}</Box>
);

type Props = {
  data?: WorkloadTrafficDestination<Big>[];
  loading?: boolean;
};

export const TopDestinationsSummary = ({ data, loading }: Props) => {
  const context = useTabsState<NetworkResourceType>();

  if (loading) {
    return (
      <Container>
        <Skeleton data-testid="top-destinations-summary-skeleton" />
      </Container>
    );
  }

  return (
    <Container>
      <Box
        display="flex"
        width="100%"
        maxWidth="100%"
        flexWrap="nowrap"
        justifyContent="center"
        p={16}
        gap={20}
      >
        {data?.map((item, index) => {
          const amount = sumResource(context!.currentTab, item);
          const color = TOP_DESTINATIONS_COLOR_MAP[index];
          return (
            <MetricsBox
              testId="top-destinations-summary-item"
              key={
                (item.workloadName || '') +
                (item.workloadType || '') +
                (item.namespace || '')
              }
              markerColor={color}
              sx={{ flex: 1, maxWidth: 112 }}
              title={
                <EllipsisWithTooltip
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    maxWidth: 100,
                  }}
                  TypographyProps={{ variant: 'L10R', lineHeight: '12px' }}
                  TooltipProps={{
                    placement: 'top-start',
                    arrow: true,
                  }}
                >
                  {item.workloadName}
                </EllipsisWithTooltip>
              }
              primaryValue={
                context!.currentTab === NetworkResourceType.COSTS ? (
                  <Money
                    amount={amount}
                    precision={getCurrencyPrecision(amount)}
                    fixed={getCurrencyPrecision(amount)}
                  />
                ) : (
                  bytesToUnit(amount, 2)
                )
              }
              primaryValueProps={{
                fontSize: 14,
                lineHeight: '22px',
              }}
            />
          );
        })}
      </Box>
    </Container>
  );
};
